import { useState } from 'react'
import { Save, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react'
import useStore from '../store/useStore'
import { getEndpointHealth } from '../api/runpod'

export default function Settings() {
  const { config, setConfig } = useStore()
  const [form, setForm] = useState({ ...config })
  const [showKeys, setShowKeys] = useState({})
  const [saved, setSaved] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)

  const handleChange = (key, value) => {
    setForm(f => ({ ...f, [key]: value }))
  }

  const handleSave = () => {
    setConfig(form)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const handleTestRunPod = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      const { online } = await getEndpointHealth()
      setTestResult({ success: online, message: online ? 'RunPod endpoint is online and responding.' : 'RunPod endpoint not reachable. Check your API key and endpoint ID.' })
    } catch (err) {
      setTestResult({ success: false, message: `Error: ${err.message}` })
    } finally {
      setTesting(false)
    }
  }

  const toggleShow = (key) => setShowKeys(s => ({ ...s, [key]: !s[key] }))

  return (
    <div className="flex-1 overflow-y-auto p-6 max-w-3xl">

      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">Settings</h2>
        <p className="text-slate-400 text-sm mt-1">Configure API connections and platform defaults.</p>
      </div>

      <div className="space-y-6">

        {/* RunPod */}
        <Section title="RunPod Serverless" subtitle="GPU compute for all AI generation">
          <Field label="API Key" required>
            <div className="relative">
              <input
                type={showKeys.runpodApiKey ? 'text' : 'password'}
                className="input pr-10"
                placeholder="rp_..."
                value={form.runpodApiKey}
                onChange={e => handleChange('runpodApiKey', e.target.value)}
              />
              <button onClick={() => toggleShow('runpodApiKey')} className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-200">
                {showKeys.runpodApiKey ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </Field>
          <Field label="ComfyUI Endpoint ID" required>
            <input
              type="text"
              className="input"
              placeholder="your-endpoint-id"
              value={form.runpodEndpointId}
              onChange={e => handleChange('runpodEndpointId', e.target.value)}
            />
          </Field>
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={handleTestRunPod}
              disabled={testing || !form.runpodApiKey || !form.runpodEndpointId}
              className="btn-secondary text-sm"
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
            {testResult && (
              <div className={`flex items-center gap-2 text-sm ${testResult.success ? 'text-green-400' : 'text-red-400'}`}>
                {testResult.success ? <CheckCircle size={15} /> : <AlertCircle size={15} />}
                {testResult.message}
              </div>
            )}
          </div>
        </Section>

        {/* Supabase */}
        <Section title="Supabase" subtitle="Database for characters, clips, and images">
          <Field label="Supabase URL" required>
            <input
              type="text"
              className="input"
              placeholder="https://yourproject.supabase.co"
              value={form.supabaseUrl}
              onChange={e => handleChange('supabaseUrl', e.target.value)}
            />
          </Field>
          <Field label="Anon Key" required>
            <div className="relative">
              <input
                type={showKeys.supabaseAnonKey ? 'text' : 'password'}
                className="input pr-10"
                placeholder="eyJ..."
                value={form.supabaseAnonKey}
                onChange={e => handleChange('supabaseAnonKey', e.target.value)}
              />
              <button onClick={() => toggleShow('supabaseAnonKey')} className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-200">
                {showKeys.supabaseAnonKey ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </Field>
        </Section>

        {/* ElevenLabs */}
        <Section title="ElevenLabs" subtitle="Voice synthesis and voice cloning">
          <Field label="API Key" required>
            <div className="relative">
              <input
                type={showKeys.elevenlabsApiKey ? 'text' : 'password'}
                className="input pr-10"
                placeholder="sk_..."
                value={form.elevenlabsApiKey}
                onChange={e => handleChange('elevenlabsApiKey', e.target.value)}
              />
              <button onClick={() => toggleShow('elevenlabsApiKey')} className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-200">
                {showKeys.elevenlabsApiKey ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </Field>
        </Section>

        {/* Save */}
        <div className="flex items-center gap-3 pt-2">
          <button onClick={handleSave} className="btn-primary">
            <Save size={16} />
            Save Settings
          </button>
          {saved && (
            <div className="flex items-center gap-2 text-green-400 text-sm">
              <CheckCircle size={15} /> Settings saved
            </div>
          )}
        </div>

      </div>
    </div>
  )
}

function Section({ title, subtitle, children }) {
  return (
    <div className="card p-5">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  )
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="label">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  )
}
